// src/components/HomePage.js
import React, { useState, useEffect } from 'react';
import authService from '../services/authService';
import config from '../config';

const VALIDATE_SNIPPET = `GET /api/keys/validate?apiKey=YOUR_KEY

Response
{
  "valid": true,
  "error": null
}`;

const RECORD_SNIPPET = `POST /api/usage/record
X-API-KEY: YOUR_KEY
Content-Type: application/json

{
  "units": 125
}`;

const DOCKER_SNIPPET = `docker run -d \\
  --name license-manager \\
  -p 8787:8787 \\
  -v license-data:/app/data \\
  ghcr.io/rigrai/tailli-license-manager:latest`;

const SEAT_SNIPPET = `from tailli_license import LicenseClient

client = LicenseClient(base_url, product_id="my-product")

with client.seat(context={"user": "alice"}) as seat:
    # Seat auto-releases when the block exits
    run_inference()`;

const BoltIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h7l-2 8 10-12h-7l2-8z" />
  </svg>
);

const PulseIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 20V10M12 20V4M6 20v-6" />
  </svg>
);

const TeamIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const AirGapIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M5 12.55a11 11 0 0114.08 0" />
    <path d="M1.42 9a16 16 0 0121.16 0" />
    <path d="M8.53 16.11a6 6 0 016.95 0" />
    <circle cx="12" cy="20" r="0.5" fill="currentColor" />
  </svg>
);

const GridIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const ServerIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="group bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-indigo-500/30 hover:bg-slate-800/60 hover:-translate-y-0.5 transition-all duration-300">
    <div className="w-10 h-10 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-semibold text-slate-100 mb-2">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
  </div>
);

const LoginForm = ({ onLogin, onSwitchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const success = await onLogin(email, password);
      if (!success) {
        setError('Invalid email or password');
      }
    } catch (error) {
      console.error("Login failed:", error);
      setError(error.message || 'An error occurred while logging in.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-500/20 text-red-200 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        required
        className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
        autoComplete="email"
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        required
        className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
        autoComplete="current-password"
      />
      <button
        type="submit"
        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded disabled:opacity-70"
        disabled={isLoading}
      >
        {isLoading ? 'Logging in...' : 'Login'}
      </button>
      <div className="text-center mt-4">
        <button 
          type="button"
          onClick={onSwitchToRegister}
          className="text-indigo-400 hover:text-indigo-300 text-sm"
        >
          Don't have an account? Register
        </button>
      </div>
    </form>
  );
};

// Registration route selector component
const RegistrationPathSelector = ({ onSelectCreateTeam, onSelectJoinTeam }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4 text-slate-100">Join Táillí</h2>
      <p className="mb-6 text-slate-400">Choose how you'd like to get started:</p>
      
      <button
        onClick={onSelectCreateTeam}
        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-5 px-4 rounded-lg mb-4 flex flex-col items-center transition-colors"
      >
        <div className="text-lg mb-1">Create a new team</div>
        <div className="text-sm text-indigo-200">Start fresh with your own team and API keys</div>
      </button>

      <button
        onClick={onSelectJoinTeam}
        className="w-full bg-slate-800 border border-slate-700 hover:border-slate-600 text-slate-100 font-semibold py-5 px-4 rounded-lg flex flex-col items-center transition-colors"
      >
        <div className="text-lg mb-1">Join an existing team</div>
        <div className="text-sm text-slate-400">I have an invitation from my organization</div>
      </button>
      
      <div className="text-center mt-6">
        <button
          onClick={() => window.history.back()}
          className="text-indigo-400 hover:text-indigo-300 text-sm"
        >
          Back to login
        </button>
      </div>
    </div>
  );
};

const extractInviteToken = (rawValue = '') => {
  let value = rawValue.trim();
  if (!value) {
    return '';
  }

  const queryMatch = value.match(/inviteToken=([A-Za-z0-9_-]+)/i);
  if (queryMatch) {
    return queryMatch[1];
  }

  const hashIndex = value.indexOf('#');
  if (hashIndex !== -1) {
    value = value.substring(0, hashIndex);
  }

  const queryIndex = value.indexOf('?');
  if (queryIndex !== -1) {
    value = value.substring(0, queryIndex);
  }

  if (value.includes('/')) {
    const segments = value.split('/').filter(Boolean);
    if (segments.length > 0) {
      value = segments[segments.length - 1];
    }
  }

  value = value.replace(/[)\].,]+$/, '').trim();
  return value;
};

const RegisterForm = ({
  onRegister,
  onRegisterWithInvite,
  onSwitchToLogin,
  invitationMode = false,
  inviteEmail = '',
  inviteToken,
  inviteDetails
}) => {
  const [email, setEmail] = useState(inviteEmail || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Team creation fields - only used in non-invitation mode
  const [teamName, setTeamName] = useState('');

  useEffect(() => {
    if (invitationMode && inviteEmail) {
      setEmail(inviteEmail);
    }
  }, [invitationMode, inviteEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    // In non-invitation mode, validate team name
    if (!invitationMode && !teamName.trim()) {
      setError('Team name is required');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (invitationMode) {
        if (!inviteToken) {
          setError('Missing invitation token. Please restart from your invite link.');
          return;
        }
        if (!onRegisterWithInvite) {
          setError('Joining by invitation is currently unavailable. Please try again later.');
          return;
        }

        await onRegisterWithInvite(email, password, inviteToken);
      } else {
        // For team creation flow, pass team name
        await onRegister(email, password, teamName);
      }
    } catch (error) {
      console.error("Registration failed:", error);
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-500/20 text-red-200 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      
      <h2 className="text-xl font-bold mb-4 text-slate-100">
        {invitationMode ? 'Join an existing team' : 'Create your account & team'}
      </h2>

      {invitationMode && inviteDetails && (
        <div className="bg-slate-800/50 border border-slate-700 p-3 rounded-lg text-sm space-y-1">
          <p className="text-slate-200">
            You&rsquo;re joining <span className="font-semibold text-slate-100">{inviteDetails.teamName}</span> as a{' '}
            <span className="font-semibold text-slate-100">{inviteDetails.role}</span>.
          </p>
          <p className="text-xs text-slate-400">
            Use the email this invite was sent to so we can link you to the right team.
          </p>
        </div>
      )}
      
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        required
        className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
        autoComplete="email"
        disabled={invitationMode && inviteEmail} // Disable if we have an invite with predefined email
      />
      
      {!invitationMode && (
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          placeholder="Team Name"
          required
          className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
        />
      )}
      
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        required
        className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
        autoComplete="new-password"
      />
      
      <input
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder="Confirm Password"
        required
        className="w-full p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600"
        autoComplete="new-password"
      />
      
      <button
        type="submit"
        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded disabled:opacity-70"
        disabled={isLoading}
      >
        {isLoading ? 'Creating account...' : (invitationMode ? 'Join Team' : 'Create Account & Team')}
      </button>
      
      <div className="text-center mt-4">
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-indigo-400 hover:text-indigo-300 text-sm"
        >
          Already have an account? Login
        </button>
      </div>
    </form>
  );
};

const HomePage = ({ onLoginSuccess }) => {
  const [currentView, setCurrentView] = useState('login'); // login, register-paths, register-create, register-join
  const [inviteLink, setInviteLink] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteDetails, setInviteDetails] = useState(null);
  const [inviteLookupError, setInviteLookupError] = useState('');
  const [inviteLookupLoading, setInviteLookupLoading] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const authenticated = await authService.isAuthenticated();
      // If authenticated, we just call the onLoginSuccess prop function
      if (authenticated) {
        onLoginSuccess(); // <-- Use onLoginSuccess here
      }
    };
    checkAuth();
  }, [onLoginSuccess]); // <-- Add onLoginSuccess to dependency array

  const handleLogin = async (email, password) => {
    try {
      const success = await authService.login(email, password);
      if (success) {
        onLoginSuccess(); // <-- Use onLoginSuccess here
        return true;
      }
      return false;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const handleRegister = async (email, password, teamName = null) => {
    try {
      if (!teamName) {
        throw new Error('Team name is required for registration');
      }
      
      // Register with team creation in a single step
      const result = await authService.register(email, password, teamName);
      
      if (result && result.success) {
        console.log('Registration successful with team:', result);
        // After successful registration, user is already logged in and has a team
        // No need to redirect to CreateTeamPage
        
        // Explicitly refresh user data to ensure the app recognizes the user has a team
        await onLoginSuccess(); // <-- Use onLoginSuccess here
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      console.error("Registration failed:", error);
      throw error;
    }
  };

  const handleInviteRegister = async (email, password, tokenOverride = null) => {
    const tokenToUse = tokenOverride || inviteToken;
    if (!tokenToUse) {
      throw new Error('Missing invitation token. Please restart from your invitation link.');
    }

    const result = await authService.registerWithInvite(email, password, tokenToUse);
    if (!result || !result.success) {
      throw new Error(result?.error || 'Failed to join the team with this invitation.');
    }

    await onLoginSuccess();
  };

  const handleInviteLinkSubmit = async () => {
    setInviteLookupError('');

    const token = extractInviteToken(inviteLink);
    if (!token) {
      setInviteLookupError('Please paste a valid invitation URL or token.');
      return;
    }

    setInviteLookupLoading(true);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/invites/${token}`);
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success || !data.invite) {
        throw new Error(data.error || 'Could not find that invitation. Double-check the link and try again.');
      }

      setInviteToken(token);
      setInviteDetails(data.invite);
      setInviteLookupError('');
      setInviteLink('');
      setCurrentView('register-join-register');
    } catch (error) {
      console.error('Invite lookup failed:', error);
      setInviteLookupError(error.message || 'Something went wrong while checking that invitation.');
    } finally {
      setInviteLookupLoading(false);
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'login':
        return (
          <LoginForm 
            onLogin={handleLogin}
            onSwitchToRegister={() => setCurrentView('register-paths')}
          />
        );
      case 'register-paths':
        return (
          <RegistrationPathSelector 
            onSelectCreateTeam={() => setCurrentView('register-create')}
            onSelectJoinTeam={() => {
              setInviteLink('');
              setInviteLookupError('');
              setInviteDetails(null);
              setInviteToken('');
              setCurrentView('register-join');
            }}
          />
        );
      case 'register-create':
        return (
          <RegisterForm 
            onRegister={handleRegister}
            onSwitchToLogin={() => setCurrentView('login')}
            invitationMode={false}
          />
        );
      case 'register-join':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-4 text-slate-100">Join an existing team</h2>
            <p className="mb-6 text-slate-300">Paste the invitation URL you received from a teammate. We&rsquo;ll confirm the details and help you create your account.</p>

            <div className="bg-slate-800/50 border border-slate-700 p-4 rounded-lg space-y-4">
              <h3 className="font-semibold text-slate-100">Have an invitation link?</h3>
              <p className="text-sm text-slate-400">
                The link usually looks like <code className="bg-slate-900 px-1.5 py-0.5 rounded text-indigo-300">https://app.tailli.io/invite/abc123</code>. You can paste the whole link or just the token.
              </p>

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleInviteLinkSubmit();
                }}
                className="space-y-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-2 sm:space-y-0">
                  <input
                    type="text"
                    value={inviteLink}
                    onChange={(e) => setInviteLink(e.target.value)}
                    placeholder="Paste invitation link or token"
                    className="flex-1 p-2 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-slate-600 text-sm"
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded disabled:opacity-70"
                    disabled={inviteLookupLoading}
                  >
                    {inviteLookupLoading ? 'Checking...' : 'Continue'}
                  </button>
                </div>
              </form>

              {inviteLookupError && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded text-sm">
                  {inviteLookupError}
                </div>
              )}

              <p className="text-xs text-slate-500">
                Already logged in with the invited email? Open the link directly to accept and finish.
              </p>
            </div>

            <div className="text-center mt-6">
              <button
                onClick={() => {
                  setInviteLink('');
                  setInviteLookupError('');
                  setInviteDetails(null);
                  setInviteToken('');
                  setCurrentView('register-paths');
                }}
                className="text-indigo-400 hover:text-indigo-300 text-sm"
              >
                Back to options
              </button>
              <div className="mt-3">
                <button
                  onClick={() => setCurrentView('login')}
                  className="text-sm text-indigo-400 hover:text-indigo-300"
                >
                  Already have an account? Log in
                </button>
              </div>
            </div>
          </div>
        );
      case 'register-join-register':
        return (
          <RegisterForm 
            onRegister={handleRegister}
            onRegisterWithInvite={handleInviteRegister}
            onSwitchToLogin={() => setCurrentView('login')}
            invitationMode
            inviteEmail={inviteDetails?.email || ''}
            inviteToken={inviteToken}
            inviteDetails={inviteDetails}
          />
        );
      default:
        return <div>Unknown view</div>;
    }
  };

  return (
    <div>
      {/* ── Hero ── */}
      <section className="relative pt-4 pb-16 lg:pt-8 lg:pb-24">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-indigo-500/[0.07] rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Copy */}
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-4 py-1.5 text-sm text-indigo-300 font-medium mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-400"></span>
              </span>
              Now in public beta
            </div>

            <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight text-white">
              API billing and licensing,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-indigo-300">
                without the plumbing
              </span>
            </h1>

            <p className="mt-6 text-lg text-slate-400 leading-relaxed">
              Cloud usage metering with sub-50 ms key validation.
              Cryptographically-signed seat licenses for air-gapped
              deployments. One dashboard for both.
            </p>

            <div className="mt-8 flex items-center gap-6">
              <a
                href="/docs/"
                className="group inline-flex items-center gap-2 text-sm font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                Read the documentation
                <span className="group-hover:translate-x-0.5 transition-transform">→</span>
              </a>
            </div>
          </div>

          {/* Auth card */}
          <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/80 rounded-xl p-8 shadow-2xl shadow-black/20">
            <h2 className="text-xl font-bold mb-2 text-slate-100">Get started free</h2>
            <p className="text-sm text-slate-400 mb-6">No credit card required during beta.</p>
            {renderCurrentView()}
          </div>
        </div>
      </section>

      {/* ── Cloud API Metering ── */}
      <section className="py-16 border-t border-slate-800/60">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Cloud API metering
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl mx-auto">
            Validate keys and track consumption at the edge. Two endpoints, zero infrastructure to manage.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <FeatureCard
            icon={<BoltIcon />}
            title="Sub-second key validation"
            description="Gate access to demos and production APIs with edge-validated checks that resolve in under 50 ms."
          />
          <FeatureCard
            icon={<PulseIcon />}
            title="Automatic usage aggregation"
            description="One API call per request. Táillí aggregates units per key, customer, product, and plan automatically."
          />
          <FeatureCard
            icon={<ChartIcon />}
            title="Ready-to-bill reporting"
            description="Visualize trends, drill into customer and product activity, then export CSV reports for your billing workflow."
          />
          <FeatureCard
            icon={<TeamIcon />}
            title="Team & customer management"
            description="Invite colleagues, create customer accounts, issue API keys, and control access — all from one dashboard."
          />
        </div>

        {/* API Preview */}
        <div className="mt-12">
          <h3 className="text-center text-xl font-bold text-white mb-2">
            Two endpoints. That's it.
          </h3>
          <p className="text-center text-slate-400 text-sm mb-8">
            Ship your integration in minutes.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs font-mono font-bold rounded">GET</span>
                <h4 className="text-sm font-semibold text-slate-200">Validate an API key</h4>
              </div>
              <p className="text-xs text-slate-400 mb-3">Check whether a key is active before accepting a request.</p>
              <pre className="bg-slate-950 border border-slate-700/50 text-emerald-400 text-xs p-4 rounded-lg overflow-x-auto whitespace-pre">{VALIDATE_SNIPPET}</pre>
            </div>

            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs font-mono font-bold rounded">POST</span>
                <h4 className="text-sm font-semibold text-slate-200">Record metered usage</h4>
              </div>
              <p className="text-xs text-slate-400 mb-3">Increment daily usage counters with a single API call.</p>
              <pre className="bg-slate-950 border border-slate-700/50 text-emerald-400 text-xs p-4 rounded-lg overflow-x-auto whitespace-pre">{RECORD_SNIPPET}</pre>
            </div>
          </div>
        </div>
      </section>

      {/* ── Air-Gapped Seat Licensing ── */}
      <section className="py-16 border-t border-slate-800/60">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Air-gapped seat licensing
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl mx-auto">
            Cryptographically-signed seat licenses for disconnected environments.
            Runs on your customer's infrastructure with zero internet dependency.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <FeatureCard
            icon={<ShieldCheckIcon />}
            title="Ed25519-signed license files"
            description="Every grant is cryptographically signed and hardware-bound. Issue .rigrlic files from your dashboard and transfer via email or USB."
          />
          <FeatureCard
            icon={<AirGapIcon />}
            title="Fully offline after activation"
            description="No internet required once activated. Purpose-built for classified networks, manufacturing floors, and regulated environments."
          />
          <FeatureCard
            icon={<GridIcon />}
            title="Concurrent seat management"
            description="Control exactly how many users run simultaneously. Seats auto-release on exit or after timeout — no stuck licenses."
          />
          <FeatureCard
            icon={<ServerIcon />}
            title="Single Docker deployment"
            description="One container on customer infrastructure. Persistent storage, built-in health checks, and zero external dependencies."
          />
        </div>

        {/* Licensing Preview */}
        <div className="mt-12">
          <h3 className="text-center text-xl font-bold text-white mb-2">
            Deploy in minutes. License forever.
          </h3>
          <p className="text-center text-slate-400 text-sm mb-8">
            A single Docker container is all your customer needs.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 text-xs font-mono font-bold rounded">docker</span>
                <h4 className="text-sm font-semibold text-slate-200">Deploy the license manager</h4>
              </div>
              <p className="text-xs text-slate-400 mb-3">Run the self-contained license manager on customer infrastructure.</p>
              <pre className="bg-slate-950 border border-slate-700/50 text-emerald-400 text-xs p-4 rounded-lg overflow-x-auto whitespace-pre">{DOCKER_SNIPPET}</pre>
            </div>

            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-yellow-500/10 text-yellow-400 text-xs font-mono font-bold rounded">python</span>
                <h4 className="text-sm font-semibold text-slate-200">Allocate seats in your app</h4>
              </div>
              <p className="text-xs text-slate-400 mb-3">Seats allocate on entry and auto-release when the block exits.</p>
              <pre className="bg-slate-950 border border-slate-700/50 text-emerald-400 text-xs p-4 rounded-lg overflow-x-auto whitespace-pre">{SEAT_SNIPPET}</pre>
            </div>
          </div>
        </div>
      </section>

      {/* ── Docs ── */}
      <p className="text-center py-8 border-t border-slate-800/60 text-sm text-slate-500">
        <a href="/docs/" className="text-indigo-400 hover:text-indigo-300 transition-colors">
          Read the full documentation
        </a>{' '}
        for integration guides, offline licensing, and best practices.
      </p>

      {/* ── Trust bar ── */}
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 py-10 border-t border-slate-800/60 text-xs text-slate-500 tracking-wide uppercase">
        <span>Built on Cloudflare Workers</span>
        <span className="hidden sm:inline">·</span>
        <span>Ed25519-signed licenses</span>
        <span className="hidden sm:inline">·</span>
        <span>Sub-50 ms edge validation</span>
      </div>
    </div>
  );
};

export default HomePage;
